import { useEffect, useState } from 'react';
import {
  Button,
  ButtonBack,
  CurrencyFormat,
  Error,
  Icon,
  Image,
  LoadingStatus,
  Option,
  SuccessStatus,
  AddBalance
} from '@/components/generic';
import { temporaryTiers } from '@/constants';
import { formatEthersToAmount, formatUnitsToAmount } from '@/utils';
import { useGetCreateCardQuote } from '@xhype/card-app-sdk';
import { useHandleCardCreation, useGetUserBalance } from '@package/smart-wallet';
import { useNavigate } from 'react-router-dom';

export const CreateNewCard = ({
  onClose,
  cardTypeData,
}: {
  onClose: () => void;
  cardTypeData?: any;
}) => {
  const [holderId, setHolderId] = useState<number | undefined>(undefined);
  const [tierId, setTierId] = useState<number | undefined>(undefined);
  const [couponCode, setCouponCode] = useState<string | undefined>(
    cardTypeData?.preselectedCoupon || undefined,
  );
  /* Handlers */
  const handleGetQuote = () => {
    if (cardTypeData?.hasCardHolder) {
      setHolderId(cardTypeData.hasCardHolder);
    }
    if (cardTypeData?.tierSelectedInfo?.id) {
      setTierId(cardTypeData.tierSelectedInfo.id);
    }
  };

  if (holderId || tierId) {
    return (
      <GetCreationQuote
        holderId={holderId}
        tierId={tierId}
        couponCode={couponCode}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <div className='mb-8 text-h4 max-md:text-h5 text-t-primary'>
        Create New Card
      </div>
      <div className='flex justify-start mb-2 gap-1'>
        <div className='shrink-0'>
          <Image
            className='aspect-3/2 w-20 rounded-xs! *:object-cover'
            src={
              temporaryTiers[
                (cardTypeData?.tierSelectedInfo
                  ?.name as keyof typeof temporaryTiers) || 'Tier 0'
              ]?.img
            }
            alt=''
          />
        </div>
        <div className='flex-1 pl-6 '>
          <div className='truncate text-h6 text-t-primary'>Google Pay</div>
          <div className='truncate text-h6 text-t-primary'>Apple Pay</div>
        </div>
        <div className='truncate font-bold text-t-secondary'>Tier {cardTypeData?.tierSelectedInfo?.name}</div>
      </div>
      <div className='w-full flex flex-row items-center justify-center gap-4 mb-8'>
        <div className='w-full relative'>
          <label className='block mb-2 text-sm font-medium text-t-primary'>
            Coupon Code
          </label>
          <div className='relative'>
            <input
              type='text'
              value={couponCode || ''}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder='Enter coupon code'
              className='w-full pl-12.5 pr-4 py-3 bg-b-surface2 border border-b-border rounded-lg text-t-primary placeholder:text-t-tertiary focus:outline-none focus:border-brand-primary transition-colors'
            />
            <div className='absolute top-1/2 -translate-y-1/2 left-1 w-10 h-10 flex items-center justify-center bg-secondary-04 rounded-full pointer-events-none'>
              <Icon className='fill-black' name='coupon' />
            </div>
          </div>
        </div>
      </div>
      <div className='mb-8 text-body-2 font-medium text-t-tertiary'>
        You're creating a new card, <br />
        <span className='text-primary-02'>Get Quote</span> and please ensure you
        have sufficient balance in your account to cover the card issuance fee.
      </div>
      <div className='flex justify-end gap-3 w-full'>
        <Button className='flex-1' isStroke type='button' onClick={onClose}>
          Cancel
        </Button>
        <Button
          className='flex-1'
          isBlack
          type='button'
          onClick={handleGetQuote}
        >
          Get card creation quote
        </Button>
      </div>
    </>
  );
};
type GetCreationQuoteProps = {
  holderId?: number;
  tierId?: number;
  couponCode?: string;
  onClose: () => void;
};
const GetCreationQuote = ({
  holderId,
  tierId = undefined,
  couponCode,
  onClose,
}: GetCreationQuoteProps) => {
  const [isSuccessTx, setIsSuccessTx] = useState<string | false>(false);
  const navigate = useNavigate();
  const { isLoading: tokenUserAmount_isLoading, tokenAmount, tokenDecimals,
    refetch: refetchUserBalance
  } = useGetUserBalance();

  const {
    isLoading,
    data: creationQuoteData,
    error: creationQuoteError,
  } = useGetCreateCardQuote({
    holderId,
    tierId,
    couponCode,
    enabled: !!holderId || !!tierId,
  });

  const {
    handleCardCreation: handleCardCreationCallback,
    isLoading: isLoadingCardCreation,
    error: cardCreationError,
  } = useHandleCardCreation();

  const handleCardCreation = async () => {
    try {
      if (!holderId) {
        console.error('holderId is empty');
        return;
      }
      const txHash = await handleCardCreationCallback(
        holderId,
        tierId!,
        couponCode,
      );
      console.log('create  successful. Transaction hash:', txHash);
      setIsSuccessTx(txHash);
    } catch (error) {
      console.error('Error creating card: ', error);
    }
  };

  useEffect(() => {
    if (creationQuoteData) {
      const interval = setInterval(() => {
        refetchUserBalance();
      }, 5000);

      return () => clearInterval(interval);
    }
    return;
  }, [refetchUserBalance, creationQuoteData]);

  if (isSuccessTx) {
    return (
      <SuccessStatus
        content='Your card has been created successfully!'
        onClose={() => navigate('/cards')}
        txHash={isSuccessTx}
      />
    );
  }
  if (isLoading || isLoadingCardCreation || tokenUserAmount_isLoading) {
    return <LoadingStatus />;
  }
  if (
    Number(
      formatUnitsToAmount({
        string: tokenAmount,
        decimalToken: tokenDecimals,
      }),
    )
    <
    Number(
      formatEthersToAmount({
        string: creationQuoteData?.amountToSend || 0,
      }),
    )
  ) {
    return (
      <>
        <Error
          error={true}
          message={
            `Insufficient balance to create a new card. Please add funds to your account. ${Number(
              formatEthersToAmount({
                string: creationQuoteData?.amountToSend || 0,
              }),
            )}`
          }
        />
        <AddBalance />
        <div className='flex flex-col w-full mb-4 border-s-border pt-4 mt-4'>
          <Option
            classTitle='w-38 [&:+]:border'
            title='Available balance'
            color='bg-primary-01'
          >
            <CurrencyFormat
              currency=''
              value={Number(
                formatUnitsToAmount({
                  string: tokenAmount,
                  decimalToken: tokenDecimals,
                }),
              )}
            />{' '}
          </Option>
          <Option
            classTitle='w-38 [&:+]:border'
            title='Amount to deposit'
            color='bg-primary-02'
          >
            <CurrencyFormat
              currency=''
              value={
                Number(
                  formatEthersToAmount({
                    string: creationQuoteData?.amountToSend || 0,
                  }),
                )
                -
                Number(
                  formatUnitsToAmount({
                    string: tokenAmount,
                    decimalToken: tokenDecimals,
                  }),
                )

              }
            />{' '}
          </Option>

        </div>
      </>

    )
  }
  /* Render */
  return (
    <>
      <ButtonBack
        title='New Quote'
        className='mb-0'
        onClick={() => onClose()}
      />
      <div className='mb-2 text-h4 max-md:text-h5 text-t-primary'>
        Quote for Creating New Card
      </div>
      <div className='mb-4 text-body-2 font-medium text-t-tertiary'>
        Please review the details below . This action can't be undone, and you
        will need to deposit the specified amount to proceed with the card
        creation. time.
      </div>
      <div className='flex flex-col w-full mb-4'>
        {/* <Option classTitle='w-38' title='Platform Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.platformFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Business Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.businessFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Provider Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.providerFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option classTitle='w-38 [&:+]:border' title='Total Fee'>
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.totalFee || 0,
              }),
            )}
          />{' '}
          USDT
        </Option> */}
        <Option
          classTitle='w-38 [&:+]:border'
          title='Amount to Send'
          color='bg-primary-04'
        >
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.amountToSend || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
        <Option
          classTitle='w-38 [&:+]:border'
          title='Amount to Receive'
          color='bg-primary-03'
        >
          <CurrencyFormat
            currency=''
            value={Number(
              formatEthersToAmount({
                string: creationQuoteData?.amountToReceive || 0,
              }),
            )}
          />{' '}
          USDT
        </Option>
      </div>
      <Error
        error={creationQuoteError}
        message={
          creationQuoteError?.message ||
          'Error While fetching card creation quote'
        }
      />

      <Error
        error={cardCreationError}
        message={
          cardCreationError ||
          'Error While creating the card. Please try again.'
        }
      />
      <div className='flex justify-end gap-3 w-full '>
        <Button className='flex-1' isStroke type='button' onClick={onClose}>
          Cancel
        </Button>
        {!creationQuoteError && (
          <Button
            className='flex-1'
            isBlack
            type='button'
            onClick={() => handleCardCreation()}
          >
            Create Card
          </Button>
        )}
      </div>
    </>
  );
};
